import { ReactNode } from 'react';
import { FormattedMessage } from '@alltrails/shared/react-intl';
import classNames from 'classnames';
// @ts-ignore
import images from 'react-payment-inputs/images';
import OutlinedTextField from '../textFields/OutlinedTextField';
import * as styles from './CreditCardForm.module.scss';

const CardValidator = require('card-validator');

type Props = {
  error: ReactNode;
  number: string;
  submitHandler: (...args: unknown[]) => unknown;
  changeHandler: any;
  setError: (error: ReactNode) => void;
};

export function validate(value: string) {
  const response = CardValidator.number(value);

  if (value.length < 1) {
    return <FormattedMessage defaultMessage="Invalid card number" />;
  }

  if (!response.isValid && response.isPotentiallyValid) {
    return <FormattedMessage defaultMessage="Your card number is incomplete." />;
  }

  if (!response.isValid) {
    return <FormattedMessage defaultMessage="Invalid card number" />;
  }

  return '';
}

function CreditCardNumber({ error, number, submitHandler, changeHandler, setError }: Props) {
  const cardInfo = CardValidator.number(number);

  function cardNumberChangeHandler(event: React.ChangeEvent<HTMLInputElement>) {
    const processedEvent = event;
    const { value } = processedEvent.target;
    processedEvent.target.value = value.replace(/\D/g, '');
    changeHandler(processedEvent);
  }

  function getCardTypeSVG() {
    const acceptedCards = ['amex', 'visa', 'mastercard', 'discover'];
    const cardImages: ReactNode[] = [];

    acceptedCards.map(cardType => {
      const cardImage = images[cardType];
      let styleKey = '';

      if (cardInfo.card) {
        let selectedCardType = cardInfo.card.type;

        if (selectedCardType === 'american-express') {
          selectedCardType = 'amex'; // annoying mismatch in type name for amex
        }

        if (selectedCardType === cardType) {
          styleKey = 'active';
        } else {
          styleKey = 'inactive';
        }
      }

      return cardImages.push(<svg className={classNames(styles.cardIcon, styles[styleKey])}>{cardImage}</svg>);
    });

    return cardImages;
  }

  function getFormattedCardNumber(cardNumber: string) {
    const response = [];
    if (cardInfo.card && cardInfo.card.lengths) {
      const numberAsArray = cardNumber.split('');
      const maxDigits = 20;
      const limit = numberAsArray.length < maxDigits ? numberAsArray.length : maxDigits - 1;
      for (let i = 0; i < limit; i++) {
        if (cardInfo.card.gaps.indexOf(i) !== -1) {
          response.push(' ');
        }
        // would be easier to do this with charCode
        if (!isNaN(parseInt(numberAsArray[i]))) {
          response.push(numberAsArray[i]);
        }
      }
    } else {
      return cardNumber;
    }

    return response.join('');
  }

  return (
    <OutlinedTextField
      name="number"
      autocomplete="cc-number"
      label={<FormattedMessage defaultMessage="Card number" />}
      adornment={getCardTypeSVG()}
      errorMessage={error}
      value={getFormattedCardNumber(number)}
      changeHandler={cardNumberChangeHandler}
      enterKeyHandler={submitHandler}
      inputMode="numeric"
      onBlur={e => setError(validate(e.target.value))}
    />
  );
}

export default CreditCardNumber;
